import { useEffect, useMemo, useState } from "react";
import {
  Loader2, Sparkles, KeyRound, ShieldAlert, Clock, Smartphone, Mail,
  // Batch 123 — the paywall's five bullets. lucide-react is already in the
  // bundle, so this adds no dependency and no chunk.
  MessagesSquare, Compass, Hand, Target, Infinity as InfinityIcon,
} from "lucide-react";
import { useBeta } from "./BetaContext";
import { betaApi } from "./betaClient";
import { formatTime } from "@/constants";

const readParam = (name) => {
  try {
    return new URLSearchParams(window.location.search).get(name) || "";
  } catch {
    return "";
  }
};

const readInviteFromUrl = () => readParam("invite");
// Batch 122 — readVerifyFromUrl is gone with VerifyLink. A code is typed into a
// card inside the app, so there is no ?verify= URL to read.

const fmtReset = (iso) => {
  if (!iso) return "tomorrow";
  try {
    const d = new Date(iso);
    const mins = Math.max(0, Math.round((d - Date.now()) / 60000));
    if (mins < 60) return `in ${mins} min`;
    return `in ${Math.round(mins / 60)}h`;
  } catch {
    return "tomorrow";
  }
};

function Shell({ icon: Icon, title, children, testId }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-wingman-bg px-4" data-testid={testId}>
      <div className="w-full max-w-md rounded-2xl border border-wingman-border bg-wingman-card p-8">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-wingman-gold/10 p-2.5">
            <Icon className="h-5 w-5 text-wingman-gold" />
          </div>
          <div>
            <p className="font-bebas text-2xl tracking-wide text-wingman-gold">SPARKCUE</p>
            <p className="text-xs text-wingman-muted">{title}</p>
          </div>
        </div>
        <div className="mt-6">{children}</div>
      </div>
    </div>
  );
}

function BootingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-wingman-bg" data-testid="beta-booting">
      <Loader2 className="h-6 w-6 animate-spin text-wingman-gold" />
    </div>
  );
}

function SuspendedScreen() {
  const { logout } = useBeta();
  return (
    <Shell icon={ShieldAlert} title="Access paused" testId="beta-suspended">
      {/* "beta" dropped, one word. A public user has access, not beta access,
          and the sentence is true for both without a branch. */}
      <p className="text-sm text-wingman-text">Your access has been paused.</p>
      <p className="mt-2 text-xs text-wingman-muted">
        If you think this is a mistake, reach out to the SparkCue team and we'll get you back in.
      </p>
      <button
        onClick={logout}
        data-testid="beta-suspended-signout"
        className="mt-6 w-full rounded-lg border border-wingman-border py-2.5 text-sm text-wingman-muted hover:text-wingman-text transition-colors"
      >
        {/* Was "Use a different code". A public user has no code and never had
            one, so that named an action they could not take -- the same defect
            e9087d3 fixed on the device-limit screen. This is what the button
            actually does (logout clears the token and returns to the gate), and
            it is the exact phrase AccountPanel already uses for it, so the app
            stops having two names for one action. */}
        Sign out on this device
      </button>
    </Shell>
  );
}

// Batch 122 — THE REGISTRATION CARD. Rendered by App.js as a sibling of the
// mounted app, never as a replacement, so the conversation the user typed is
// still in state behind it and abandoning the card loses nothing.
//
// Two steps: email, then the six-digit code from the mail. `lead` and `sub` let
// the caller say why the card appeared (a generate, or the screenshot cap)
// without forking the component.
export function GateDoors({ onRegistered, lead, sub, initialSentTo, onCodeSent }) {
  const { redeem, signup, verifySignup, revokeWithCode } = useBeta();
  const urlCode = useMemo(readInviteFromUrl, []);
  // The invite door sits behind a line: codeless is the majority arrival now.
  // Arriving WITH a code opens it immediately, so an invite link still works in
  // one tap.
  const [showCode, setShowCode] = useState(!!urlCode);
  const [code, setCode] = useState(urlCode);
  const [email, setEmail] = useState("");
  const [needEmail, setNeedEmail] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // Batch 126 — seeded from the caller so dismissing the card and reopening it
  // returns to the CODE step rather than the email step. The code is live for 15
  // minutes and sending a second one upserts over it, so landing back on step
  // one invites the user to kill the code sitting in their inbox.
  const [sentTo, setSentTo] = useState(initialSentTo || "");   // step 2 once set
  const [digits, setDigits] = useState("");
  const [attemptsLeft, setAttemptsLeft] = useState(null);
  const [lockedDevices, setLockedDevices] = useState(null);
  const [lockedMax, setLockedMax] = useState(null);

  const succeeded = () => { if (onRegistered) onRegistered(); };

  const deviceLimit = (detail) => {
    setLockedDevices(detail.devices || []);
    setLockedMax(detail.max_devices);
    setErr(`You're already signed in on ${detail.max_devices} devices.`);
  };

  const submit = async (e) => {
    if (e) e.preventDefault();
    const c = code.trim().toUpperCase();
    const em = email.trim();
    setErr("");
    setLockedDevices(null);
    // An invite code, when present, wins: it is the more specific intent.
    if (showCode && c) {
      setBusy(true);
      try {
        await redeem(c, em, null);
        succeeded();
      } catch (ex) {
        const st = ex?.response?.status;
        const detail = ex?.response?.data?.detail;
        if (st === 400) { setNeedEmail(true); setErr("This invite needs your email to continue."); }
        else if (st === 409 && detail?.error === "device_limit_reached") deviceLimit(detail);
        else if (st === 404) setErr("That invite code isn't valid or has been revoked.");
        else if (st === 403) setErr("This account is paused. Contact the SparkCue team.");
        else setErr("Something went wrong. Please try again.");
      } finally { setBusy(false); }
      return;
    }
    if (!em) return;
    setBusy(true);
    try {
      await signup(em);
      setSentTo(em);
      setDigits("");
      setAttemptsLeft(null);
      // Told to the caller so it survives a dismiss. Only the address: digits
      // and attemptsLeft are cheap to re-derive, and the server holds the real
      // attempt counter, so nothing is refunded by reopening.
      if (onCodeSent) onCodeSent(em);
    } catch (ex) {
      const st = ex?.response?.status;
      const detail = ex?.response?.data?.detail;
      if (st === 429) setErr("That's a few codes already. Give it an hour, then try again.");
      else if (st === 422) setErr("That email address doesn't look right.");
      // Batch 126 — UNREACHABLE TODAY AND DELIBERATELY PRESENT. /auth/signup
      // cannot return a 409: the device cap lives in _beta_mint_session, which
      // only /beta/redeem and /auth/verify reach. Without this branch the
      // fallback below reports a mail failure for a device cap, naming the wrong
      // problem and pointing at a retry that cannot work. That is a trap set for
      // whoever adds a check to that route later.
      else if (st === 409 && detail?.error === "device_limit_reached") deviceLimit(detail);
      else setErr("We couldn't send that email. Please try again.");
    } finally { setBusy(false); }
  };

  const submitCode = async (e) => {
    if (e) e.preventDefault();
    const d = digits.replace(/\D/g, "");
    if (d.length !== 6) return;
    setBusy(true);
    setErr("");
    setLockedDevices(null);
    try {
      await verifySignup(sentTo, d);
      succeeded();
    } catch (ex) {
      const st = ex?.response?.status;
      const detail = ex?.response?.data?.detail;
      if (st === 401 && detail?.error === "code_wrong") {
        const left = detail.attempts_left;
        setAttemptsLeft(left);
        // Showing the count helps someone who fat-fingered a digit far more than
        // it helps an attacker: at five tries on a million-value space, knowing
        // how many are left is worth close to nothing to them.
        setErr(`That code isn't right. ${left} attempt${left === 1 ? "" : "s"} left.`);
      } else if (st === 410 && detail?.error === "code_expired") {
        setErr("That code has expired. Codes last 15 minutes, send a new one.");
      } else if (st === 410) {
        setErr("That code is used up. Send a new one and we'll try again.");
        setAttemptsLeft(0);
      } else if (st === 409 && detail?.error === "device_limit_reached") {
        deviceLimit(detail);
      } else if (st === 403) {
        setErr("This account is paused. Contact the SparkCue team.");
      } else {
        setErr("Something went wrong. Please try again.");
      }
    } finally { setBusy(false); }
  };

  // One-tap: auto-redeem when arriving via an invite link. Unchanged behaviour.
  // There is no longer a second auto-submitting component to collide with -- the
  // ?verify= flow and VerifyLink are gone.
  useEffect(() => {
    if (urlCode) submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Batch 127 — TWO VARIANTS, and `sentTo` is what tells them apart for free.
  //
  // A verify-path 409 can only reach step 2, where sentTo is set and a live code
  // is in the user's hands. A redeem-path 409 can only reach step 1, where it is
  // empty. Only the first can authorise a revoke: an invite code is long-lived,
  // reusable, and may have been handed to them by someone else, which is a
  // weaker proof for a destructive action than a fifteen-minute single-use code
  // sent to the address the account is keyed on.
  //
  // So the invite variant keeps the other-device route, because it is genuinely
  // all that user has, and keeps the exact strings betaDeviceLimit.test.js
  // checks against the components that really render them.
  const canRevokeHere = !!sentTo;

  const revokeOne = async (deviceId) => {
    setBusy(true);
    setErr("");
    try {
      await revokeWithCode(sentTo, digits.replace(/\D/g, ""), deviceId);
      setLockedDevices((prev) => (prev || []).filter((d) => d.id !== deviceId));
    } catch (ex) {
      const st = ex?.response?.status;
      const detail = ex?.response?.data?.detail;
      if (st === 410 && detail?.error === "code_expired") {
        setErr("That code has expired. Codes last 15 minutes, send a new one.");
      } else if (st === 410) {
        setErr("That code is used up. Send a new one and we'll try again.");
      } else if (st === 401) {
        setErr("That code isn't right. Check it and try again.");
      } else if (st === 429) {
        setErr("That's a lot of sign-outs. Give it a minute.");
      } else {
        setErr("We couldn't sign that device out. Please try again.");
      }
    } finally { setBusy(false); }
  };

  const deviceBlock = lockedDevices && (
    <div className="rounded-lg border border-wingman-border bg-wingman-bg p-3" data-testid="beta-device-lock">
      <p className="text-xs text-wingman-muted">
        {canRevokeHere ? (
          <>
            You're signed in on {lockedMax ?? ""} devices. Sign one out below and you can carry on
            with the code you just entered. Or ask the SparkCue team to disconnect one.
          </>
        ) : (
          <>
            You're signed in on these already. On one you can still open, go to History, then Your
            devices, and tap Sign out next to a different one. Or ask the SparkCue team to
            disconnect one.
          </>
        )}
      </p>
      <ul className="mt-2 space-y-1.5">
        {lockedDevices.map((d) => (
          <li key={d.id} className="flex items-center gap-2 text-xs text-wingman-text">
            <Smartphone size={12} className="text-wingman-muted" />
            {/* formatTime, not fmtReset: this is a PAST timestamp. fmtReset
                clamps with Math.max(0, ...), so every device rendered "last
                active in 0 min" and no two rows could be told apart. */}
            <span className="flex-1 min-w-0">
              {d.device_type} · last active {d.last_active ? formatTime(d.last_active) : "recently"}
            </span>
            {canRevokeHere && (
              // The SAME label AccountPanel uses for the identical action, and
              // the same testid shape. One name for one action; two names for
              // one action is the defect betaDeviceLimit.test.js exists to stop.
              <button
                type="button"
                onClick={() => revokeOne(d.id)}
                disabled={busy}
                data-testid={`beta-revoke-device-${d.id}`}
                className="shrink-0 rounded-md border border-wingman-border px-2 py-1 text-[11px] text-wingman-muted hover:text-wingman-text disabled:opacity-50"
              >
                Sign out
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );

  // ---- Step 2: the code ---------------------------------------------------
  if (sentTo) {
    return (
      <Shell icon={Mail} title="Check your email" testId="beta-check-email">
        <p className="text-sm text-wingman-text">We sent a six-digit code to {sentTo}.</p>
        <p className="mt-1 text-xs text-wingman-muted">It expires in 15 minutes.</p>
        <form onSubmit={submitCode} className="mt-6 space-y-3">
          <input
            value={digits}
            onChange={(e) => setDigits(e.target.value)}
            placeholder="000000"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            maxLength={7}
            data-testid="beta-code-digits"
            className="w-full rounded-lg border border-wingman-border bg-wingman-bg px-3 py-2.5 text-center text-lg tracking-[0.4em] text-wingman-text focus:border-wingman-gold/50 focus:outline-none"
          />
          {err && <p className="text-sm text-red-400" data-testid="beta-redeem-error">{err}</p>}
          {deviceBlock}
          <button
            type="submit"
            disabled={busy}
            data-testid="beta-verify-submit"
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-wingman-gold py-2.5 text-sm font-semibold text-black transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {busy ? "Checking…" : "Confirm and get my reply"}
          </button>
        </form>
        <button
          onClick={() => { setSentTo(""); setErr(""); setDigits(""); setAttemptsLeft(null); }}
          data-testid="beta-resend"
          className="mt-3 w-full rounded-lg border border-wingman-border py-2.5 text-sm text-wingman-muted hover:text-wingman-text transition-colors"
        >
          {attemptsLeft === 0 ? "Send a new code" : "Didn't arrive? Send a new code."}
        </button>
      </Shell>
    );
  }

  // ---- Step 1: the email --------------------------------------------------
  return (
    <Shell icon={KeyRound} title="One thing before your reply" testId="beta-redeem">
      <p className="text-sm text-wingman-text" data-testid="beta-gate-lead">
        {lead || "SparkCue needs an email so your conversations and your replies stay yours. No password, no spam."}
      </p>
      <p className="mt-1 text-xs text-wingman-muted" data-testid="beta-gate-sub">
        {sub || "We'll send you a six-digit code."}{" "}
        {!showCode && (
          <button
            type="button"
            onClick={() => setShowCode(true)}
            data-testid="beta-show-code"
            className="underline decoration-dotted hover:text-wingman-text transition-colors"
          >
            Got an invite code? Use that instead.
          </button>
        )}
      </p>
      <form onSubmit={submit} className="mt-6 space-y-3">
        {showCode && (
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="SPARK-XXXX-XXXX"
            autoFocus
            data-testid="beta-code-input"
            className="w-full rounded-lg border border-wingman-border bg-wingman-bg px-3 py-2.5 text-sm uppercase tracking-wider text-wingman-text placeholder:normal-case focus:border-wingman-gold/50 focus:outline-none"
          />
        )}
        {(!showCode || needEmail) && (
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoFocus={!showCode}
            data-testid="beta-email-input"
            className="w-full rounded-lg border border-wingman-border bg-wingman-bg px-3 py-2.5 text-sm text-wingman-text focus:border-wingman-gold/50 focus:outline-none"
          />
        )}
        {err && <p className="text-sm text-red-400" data-testid="beta-redeem-error">{err}</p>}
        {deviceBlock}
        <button
          type="submit"
          disabled={busy}
          data-testid="beta-redeem-submit"
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-wingman-gold py-2.5 text-sm font-semibold text-black transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {busy ? (showCode && code.trim() ? "Unlocking…" : "Sending…")
                : (showCode && code.trim() ? "Unlock SparkCue" : "Send me a code")}
        </button>
      </form>
      {/* Batch 134 — the email is collected on THIS screen, which is what makes
          the notice due here rather than at checkout. Deliberately placed in
          the step-1 return and NOT in Shell: Shell is shared with the code step
          and with SuspendedScreen, and a link on the screen that tells someone
          their account is closed is noise at best.

          Batch 135 — the age statement and the terms link join it. Registering
          creates an account and binds the user to the rules, so the rules are
          due at THIS moment, not only at payment where they were already
          linked. The age requirement is section 1 of the document and was
          stated nowhere a user would ever see it, which made it a claim inside
          a file rather than something anyone was told.

          THE CONFIRMATION IS THE ACT OF CONTINUING. Two stricter forms were
          considered and rejected: a required checkbox, which adds a second
          required interaction to the one screen deliberately kept to a single
          input, and a date of birth field, which is real friction on a signup
          that has none. Both are stronger evidence of consent and both were
          declined for that reason. The lighter form is a decision, not an
          oversight, and a checkbox appearing here later would silently reverse
          it. ageStatement.test.js asserts neither control exists.

          THE TWO VERBS DIFFER ON PURPOSE. You AGREE TO the terms, because that
          is a contract. You are TOLD ABOUT the privacy policy, because that is
          a notice describing what happens to your data and not something you
          sign. Collapsing both into one "agree to" clause is shorter and
          legally sloppier, and a test pins the split. */}
      <p className="mt-4 text-center text-xs text-wingman-muted" data-testid="beta-legal">
        By continuing you confirm you are 18 or older and agree to our{" "}
        <a href="/terms" data-testid="beta-terms-link"
           className="underline transition-colors hover:text-wingman-text">Terms</a>.
        {" "}We only use your email to sign you in, as described in our{" "}
        <a href="/privacy" data-testid="beta-privacy-link"
           className="underline transition-colors hover:text-wingman-text">Privacy Policy</a>.
      </p>
    </Shell>
  );
}


// Batch 123 — the paywall a public user hits at turn six. It REPLACES the daily
// modal for the lifetime case; ruling 7 keeps that modal unchanged for beta
// users, whose cap really does reset.
//
// The buttons render and carry NO onClick. Payments are not wired, and a button
// that answers a tap with nothing is worse than one that visibly cannot be
// tapped yet -- the line under it already says why.
const PAYWALL_BULLETS = [
  [MessagesSquare, "Reads the whole thread, not just her last line"],
  [Compass, "Coaches the move, and shows you what it saw"],
  [Hand, "Says stop when nothing is the right reply"],
  [Target, "Plays for the goal: her number, or the date"],
  [InfinityIcon, "Unlimited replies and screenshots"],
];

function Paywall() {
  const { setLimitInfo } = useBeta();
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  // Batch 140 — THE PLAN NAME, NEVER A PRICE. The price ids live on the server
  // and STRIPE_PRICES is the allowlist: a client that could name a price could
  // name a cheaper one. This posts a plan and follows the URL Stripe returns.
  const checkout = async (plan) => {
    if (busy) return;
    setBusy(plan); setErr("");
    try {
      const { url } = await betaApi.checkout(plan);
      // Batch 142 — CLEARED BEFORE THE NAVIGATION. Same defect as the account
      // panel's portal control and worse here: all three controls below carry
      // disabled={!!busy}, so one refused or delayed navigation disabled the
      // entire purchase path until the user reloaded, and nothing on screen
      // said to.
      if (url) { setBusy(""); window.location.href = url; return; }
      setErr("Could not start checkout. Please try again.");
    } catch {
      setErr("Could not start checkout. Please try again.");
    }
    setBusy("");
  };

  // The independent half. A page restored from the back/forward cache comes
  // back with its state intact and no handler running to clear it. persisted
  // only, because pageshow also fires on the first paint and clearing there
  // would reopen the double-tap the guard above closes.
  useEffect(() => {
    const onShow = (e) => { if (e.persisted) setBusy(""); };
    window.addEventListener("pageshow", onShow);
    return () => window.removeEventListener("pageshow", onShow);
  }, []);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center overflow-y-auto bg-black/80 px-4 py-8"
         data-testid="beta-paywall">
      <div className="w-full max-w-sm rounded-2xl border border-wingman-border bg-wingman-card p-6">
        <h3 className="font-bebas text-3xl leading-tight text-wingman-text" data-testid="paywall-heading">
          That's your five free replies.
        </h3>
        <p className="mt-2 text-sm text-wingman-muted" data-testid="paywall-sub">
          Every other app writes you a message. SparkCue reads the conversation and calls the move.
        </p>

        <ul className="mt-5 space-y-2.5">
          {PAYWALL_BULLETS.map(([Icon, text], i) => (
            <li key={text} className="flex items-start gap-2.5 text-sm text-wingman-text"
                data-testid={`paywall-bullet-${i}`}>
              <Icon size={15} className="mt-0.5 shrink-0 text-wingman-gold" />
              <span>{text}</span>
            </li>
          ))}
        </ul>

        <div className="mt-6 space-y-2.5">
          <button type="button" onClick={() => checkout("monthly")} disabled={!!busy}
               data-testid="paywall-plan-monthly"
               className="flex w-full items-center justify-between rounded-xl border border-wingman-border bg-wingman-bg px-4 py-3 disabled:opacity-60">
            <span className="text-sm text-wingman-text">Monthly</span>
            <span className="text-sm font-semibold text-wingman-text">$9.99</span>
          </button>
          <button type="button" onClick={() => checkout("yearly")} disabled={!!busy}
               data-testid="paywall-plan-yearly"
               className="flex w-full items-center justify-between rounded-xl border border-wingman-gold/60 bg-wingman-gold/5 px-4 py-3 disabled:opacity-60">
            <span className="flex items-center gap-2 text-sm text-wingman-text">
              Yearly
              <span className="rounded-full bg-wingman-gold px-2 py-0.5 text-[10px] font-bold tracking-wide text-black">
                BEST VALUE
              </span>
            </span>
            <span className="text-right">
              <span className="block text-sm font-semibold text-wingman-text">$59.99</span>
              <span className="block text-[11px] text-wingman-muted">$5 a month</span>
            </span>
          </button>
        </div>

        <button
          type="button"
          onClick={() => checkout("yearly")}
          disabled={!!busy}
          data-testid="paywall-cta"
          className="mt-5 w-full rounded-lg bg-wingman-gold py-3 text-center text-sm font-semibold text-black disabled:opacity-60"
        >
          {busy ? "Opening checkout…" : "Get unlimited"}
        </button>
        {err && <p className="mt-2 text-center text-xs text-red-400" data-testid="paywall-error">{err}</p>}
        {/* Batch 140 — was "Payments open shortly." Checkout shipped, so the
            line under a payment button became the reassurance that belongs
            there instead of an apology for not having one. The promise is
            terms.md section 4, word for word in substance. */}
        <p className="mt-2 text-center text-xs text-wingman-muted" data-testid="paywall-note">
          Cancel any time. You keep access until the period you've paid for ends.
        </p>

        {/* Batch 134 — Stripe requires both documents reachable BEFORE payment,
            and this is the last surface before it. ANCHORS, never buttons with
            onClick: paywall.test.js pins that no control on this card responds
            to a tap while payments are unwired, and its own comment records
            that React 18 installs a noop on the DOM node even for delegated
            events, so an onClick here would turn that test red from another
            file. A NEW element too, never text appended into paywall-note,
            which is asserted byte-exact. */}
        <p className="mt-3 text-center text-xs text-wingman-muted" data-testid="paywall-legal">
          By subscribing you agree to our{" "}
          <a href="/terms" data-testid="paywall-terms-link"
             className="underline transition-colors hover:text-wingman-text">Terms</a>
          {" "}and{" "}
          <a href="/privacy" data-testid="paywall-privacy-link"
             className="underline transition-colors hover:text-wingman-text">Privacy Policy</a>.
        </p>

        {/* Deliberately low-prominence and deliberately present. Rereading saved
            conversations still works, so a dismiss-only dead end would be the
            wrong shape -- but so would no way back at all. */}
        <button
          onClick={() => setLimitInfo(null)}
          data-testid="paywall-close"
          className="mt-4 w-full text-center text-xs text-wingman-muted hover:text-wingman-text transition-colors"
        >
          Back to my conversations
        </button>
      </div>
    </div>
  );
}

function DailyLimitModal() {
  const { limitInfo, setLimitInfo } = useBeta();
  if (!limitInfo) return null;
  // Batch 123 — the code chooses. The interceptor passes `detail` through for
  // exactly this: the two rejections mean different things, and only one of them
  // can honestly promise a reset.
  if (limitInfo.error === "free_limit_reached") return <Paywall />;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 px-4" data-testid="beta-limit-modal">
      <div className="w-full max-w-sm rounded-2xl border border-wingman-border bg-wingman-card p-6 text-center">
        <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-wingman-gold/10">
          <Clock className="h-5 w-5 text-wingman-gold" />
        </div>
        <h3 className="mt-4 font-bebas text-2xl text-wingman-text">You're all caught up for today</h3>
        <p className="mt-2 text-sm text-wingman-muted">
          You've used all {limitInfo.limit ?? ""} of today's analyses. Your limit resets {fmtReset(limitInfo.resets_at)}.
          You can still read and revisit your saved conversations.
        </p>
        <button
          onClick={() => setLimitInfo(null)}
          data-testid="beta-limit-close"
          className="mt-6 w-full rounded-lg bg-wingman-gold py-2.5 text-sm font-semibold text-black hover:opacity-90"
        >
          Got it
        </button>
      </div>
    </div>
  );
}

// Batch 140 — THE RETURN FROM STRIPE.
//
// Stripe redirects the browser to success_url immediately and delivers the
// webhook independently, usually a second or two later and sometimes much
// longer. So a user can land back here having paid and still be capped. The
// ?checkout=return parameter is also attacker-typeable, so it SELECTS this
// screen and proves nothing: the only thing that ends the wait is the server
// reporting unlimited.
//
// Nothing here says the payment succeeded, because the client cannot know that.
// "You don't need to pay again" is the load-bearing sentence: a user who sees a
// paywall after paying will pay twice.
function ConfirmingScreen({ onDone }) {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    let alive = true;
    const slowAt = setTimeout(() => alive && setSlow(true), 12000);
    const poll = setInterval(async () => {
      try {
        const u = await betaApi.usage();
        if (alive && u && u.unlimited) onDone();
      } catch { /* keep waiting */ }
    }, 2500);
    return () => { alive = false; clearInterval(poll); clearTimeout(slowAt); };
  }, [onDone]);
  return (
    <Shell icon={Sparkles} title="One moment" testId="billing-confirming">
      <p className="text-sm text-wingman-text">Confirming your payment</p>
      <p className="mt-2 text-xs text-wingman-muted">
        Stripe has you. We're waiting on the confirmation, which usually takes a few seconds.
        You don't need to pay again.
      </p>
      {slow && (
        <p className="mt-3 text-xs text-wingman-muted" data-testid="billing-confirming-slow">
          Still confirming. Your payment went through and this will unlock on its own.
          If it hasn't in a few minutes, email sparkcue0@gmail.com.
        </p>
      )}
    </Shell>
  );
}

export function BetaGate({ children }) {
  const { enforced, status } = useBeta();
  const [confirming, setConfirming] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get("checkout") === "return";
    } catch { return false; }
  });
  if (enforced && status === "booting") return <BootingScreen />;
  // Batch 122 — THE "gate" BRANCH IS GONE, and its absence is the change.
  //
  // "gate" now falls through to the return below and renders the app, because
  // registration moved from the front door to the first generate: an
  // unregistered visitor fills in a conversation, taps generate, and is asked
  // for an email AT THAT MOMENT (App.js's registration interrupt).
  //
  // "booting" and "suspended" still replace the app, and both are right to.
  // Booting is the pre-config moment and rendering the app there would flash a
  // screen that might then be replaced. Suspended is an account that exists and
  // has been turned off -- there is no unregistered version of that state.
  //
  // The status machine keeps all four values and all four meanings; only what
  // "gate" RENDERS changed. Nothing else in the app reads `status`.
  if (enforced && status === "suspended") return <SuspendedScreen />;
  if (confirming) return <ConfirmingScreen onDone={() => setConfirming(false)} />;
  return (
    <>
      {children}
      <DailyLimitModal />
    </>
  );
}
